import { useEffect, useState } from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Eye, MessageCircle, Target, TrendingUp, Users } from "lucide-react";
import { http } from "../../lib/api";

const COLORS = ["#F26522", "#0B2D53", "#C8102E", "#2B5FA8", "#15A362", "#987715", "#7A8694", "#5B3E96"];

const Kpi = ({ icon: Icon, label, value, hint, testId }) => (
  <div className="stat-card" data-testid={testId}><span><Icon /></span><small>{label}</small><strong>{value}</strong><em>{hint}</em></div>
);

const Card = ({ title, children, className = "", testId }) => (
  <div className={`chart-card ${className}`} data-testid={testId}><h3>{title}</h3>{children}</div>
);

const Ranked = ({ rows, empty }) => rows.length ? (
  <ul className="ranked">{rows.map((r, i) => { const max = rows[0].value || 1; return <li key={r.name}><span className="ranked-name">{r.name}</span><span className="ranked-bar"><i style={{ width: `${(r.value / max) * 100}%`, background: COLORS[i % COLORS.length] }} /></span><strong>{r.value}</strong></li>; })}</ul>
) : <div className="chart-empty">{empty}</div>;

function Funnel({ stages }) {
  const top = stages[0]?.value || 1;
  return (
    <div className="funnel" data-testid="conversion-funnel">
      {stages.map((s, i) => (
        <div className="funnel-row" key={s.stage}>
          <span className="funnel-label">{s.stage}</span>
          <span className="funnel-track"><i style={{ width: `${Math.max((s.value / top) * 100, s.value ? 4 : 0)}%`, opacity: 1 - i * 0.14 }} /></span>
          <strong>{s.value}</strong>
          <em>{i === 0 ? "100%" : `${top ? ((s.value / top) * 100).toFixed(1) : 0}%`}</em>
        </div>
      ))}
    </div>
  );
}

export default function AnalyticsTab() {
  const [days, setDays] = useState(14);
  const [d, setD] = useState(null);
  useEffect(() => { http.get(`/analytics/summary?days=${days}`).then((r) => setD(r.data)).catch(() => {}); }, [days]);
  if (!d) return <div className="chart-empty" data-testid="analytics-loading">Loading analytics…</div>;
  const t = d.totals;
  return (
    <>
      <div className="admin-top"><h1>Traffic & conversion</h1>
        <select className="range-select" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="analytics-range-select">{[7, 14, 30, 90].map((n) => <option key={n} value={n}>{`Last ${n} days`}</option>)}</select>
      </div>
      <div className="stat-grid five">
        <Kpi icon={Eye} label="Page views" value={t.visits} hint="Public site visits" testId="kpi-visits" />
        <Kpi icon={Users} label="Unique visitors" value={t.unique_visitors} hint="By browser" testId="kpi-unique" />
        <Kpi icon={Target} label="Leads captured" value={t.leads} hint="All forms" testId="kpi-leads" />
        <Kpi icon={TrendingUp} label="Visitor → lead" value={`${t.conversion_rate}%`} hint={`${t.converted} converted deals`} testId="kpi-conversion" />
        <Kpi icon={MessageCircle} label="WhatsApp clicks" value={t.whatsapp_clicks} hint="Tracked taps" testId="kpi-whatsapp" />
      </div>
      <div className="chart-grid">
        <Card title="Daily traffic, leads & WhatsApp" className="wide" testId="traffic-chart">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={d.daily} margin={{ left: -20, right: 8, top: 8 }}>
              <defs><linearGradient id="gv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0B2D53" stopOpacity={0.35} /><stop offset="100%" stopColor="#0B2D53" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="#EDF0F3" vertical={false} /><XAxis dataKey="date" tick={{ fontSize: 10 }} /><YAxis allowDecimals={false} tick={{ fontSize: 10 }} /><Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="visits" name="Visits" stroke="#0B2D53" fill="url(#gv)" strokeWidth={2} />
              <Area type="monotone" dataKey="leads" name="Leads" stroke="#F26522" fill="#F2652222" strokeWidth={2} />
              <Area type="monotone" dataKey="whatsapp" name="WhatsApp" stroke="#15A362" fill="transparent" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Lead source breakdown" testId="lead-source-chart">
          {d.lead_sources.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart><Pie data={d.lead_sources} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>{d.lead_sources.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} /></PieChart>
            </ResponsiveContainer>
          ) : <div className="chart-empty">No leads in this period.</div>}
        </Card>
        <Card title="Conversion funnel" className="wide" testId="funnel-card"><Funnel stages={d.funnel} /></Card>
        <Card title="Traffic sources" testId="traffic-sources-card">
          {d.traffic_sources.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={d.traffic_sources} layout="vertical" margin={{ left: 10, right: 16 }}><XAxis type="number" hide allowDecimals={false} /><YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10 }} /><Tooltip /><Bar dataKey="value" name="Visits" fill="#0B2D53" radius={[0, 3, 3, 0]} /></BarChart>
            </ResponsiveContainer>
          ) : <div className="chart-empty">No visits yet.</div>}
        </Card>
        <Card title="Top pages" testId="top-pages-card"><Ranked rows={d.top_pages} empty="No visits yet." /></Card>
        <Card title="Lead forms" testId="lead-forms-card"><Ranked rows={d.lead_forms} empty="No leads yet." /></Card>
        <Card title="Most requested products" testId="top-products-card"><Ranked rows={d.top_products} empty="No product quotes yet." /></Card>
      </div>
    </>
  );
}
